import {CommentBox, type CommentBoxConfig, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {ReadonlyCommentBox} from '@github-ui/comment-box/ReadonlyCommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {LazyContributorFooter} from '@github-ui/contributor-footer/LazyContributorFooter'
import {type CloseButtonState, IssueActions} from '@github-ui/issue-actions/IssueActions'
import {commitCloseIssueMutation, commitReopenIssueMutation} from '@github-ui/issue-actions/UpdateIssueStateMutation'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {IS_BROWSER} from '@github-ui/ssr-utils'
import {testIdProps} from '@github-ui/test-id-props'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {LockIcon} from '@primer/octicons-react'
import {Box, Heading} from '@primer/react'
import {forwardRef, useCallback, useMemo, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
import {ConnectionHandler} from 'relay-runtime'

import {BUTTON_LABELS} from '../../constants/button-labels'
import {IDS} from '../../constants/dom-elements'
import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {type MarkdownComposerRef, useMarkdownBody} from '../../hooks/use-markdown-body'
import {useMarkdownStyles} from '../../hooks/use-markdown-styles'
import {useMetaAndShiftKeys} from '../../hooks/use-meta-and-shift-keys'
import {addCommentMutation} from '../../mutations/add-comment-mutation'
import {blockedCommentingReason} from '../../utils/blocked-commenting-reason'
import type {IssueCommentComposer$key} from './__generated__/IssueCommentComposer.graphql'

type IssueCommentComposerBaseProps = {
  onChange: () => void
  onSave: () => void
  onCancel: () => void
  onNewIssueComment?: () => void
  commentBoxConfig?: CommentBoxConfig
  fileUploadsEnabled?: boolean
  singleKeyShortcutEnabled: boolean
}

type IssueCommentComposerProps = IssueCommentComposerBaseProps & {
  issue: IssueCommentComposer$key
}

export const IssueCommentComposer = forwardRef<MarkdownComposerRef, IssueCommentComposerProps>(
  (
    {
      onChange,
      onSave,
      onCancel,
      onNewIssueComment,
      commentBoxConfig,
      singleKeyShortcutEnabled,
      issue,
      fileUploadsEnabled,
    },
    ref,
  ) => {
    const data = useFragment(
      graphql`
        fragment IssueCommentComposer on Issue {
          id
          locked
          viewerCanComment
          viewerCanClose
          viewerCanReopen
          databaseId
          repository {
            id
            databaseId
            isArchived
            nameWithOwner
            slashCommandsEnabled
          }
          ...IssueActions
        }
      `,
      issue,
    )
    const {id: issueId} = data
    const commentBoxRef = useRef<CommentBoxHandle>(null)
    const commentComposerRef = useRef<HTMLDivElement>(null)
    const repositoryDatabaseId = data.repository.databaseId!
    const isRepoArchived = data.repository.isArchived
    const {addToast} = useToastContext()
    const environment = useRelayEnvironment()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [closeButtonState, setCloseButtonState] = useState<CloseButtonState>('OPEN')
    const {markdownStyles} = useMarkdownStyles(commentComposerRef)
    const cantCommentReason = blockedCommentingReason(isRepoArchived, data.locked)
    const {metaAndShiftKeysPressed} = useMetaAndShiftKeys()
    const {markdownBody, resetMarkdownBody, markdownValidationResult, handleMarkdownBodyChanged} = useMarkdownBody({
      commentBoxRef,
      markdownComposerRef: ref,
      onChange,
      onCancel,
      referenceId: issueId,
      hasSingleKeyShortcutEnabled: singleKeyShortcutEnabled,
    })

    const onCommentSave = useCallback(
      (subject: string) => {
        setIsSubmitting(true)
        if (markdownBody?.length > 0) {
          const issueCommentsConnectionId = ConnectionHandler.getConnectionID(
            data.id, // passed as input to the mutation/subscription
            'IssueBacksideTimeline_timelineItems',
          )
          const connectionId = `${issueCommentsConnectionId}(visibleEventsOnly:true)`

          addCommentMutation({
            environment,
            input: {
              subject,
              body: markdownBody,
              connectionId,
            },
            onError: () => {
              setIsSubmitting(false)
              addToast({
                type: 'error',
                message: ERRORS.couldNotComment,
              })
            },
            onCompleted: () => {
              setIsSubmitting(false)
              resetMarkdownBody()
              onNewIssueComment && onNewIssueComment()
            },
          })
        }

        if (metaAndShiftKeysPressed) {
          if (closeButtonState === 'CLOSED' && data.viewerCanClose) {
            commitCloseIssueMutation({
              environment,
              input: {
                issueId: data?.id,
                newStateReason: 'COMPLETED',
              },
              onError: () =>
                addToast({
                  type: 'error',
                  message: ERRORS.couldNotCloseIssue,
                }),
              onCompleted: onNewIssueComment,
            })
          } else if (closeButtonState === 'NOT_PLANNED' && data.viewerCanClose) {
            commitCloseIssueMutation({
              environment,
              input: {
                issueId: data?.id,
                newStateReason: 'NOT_PLANNED',
              },
              onError: () =>
                addToast({
                  type: 'error',
                  message: ERRORS.couldNotCloseIssue,
                }),
              onCompleted: onNewIssueComment,
            })
          } else if (closeButtonState === 'OPEN' && data.viewerCanReopen) {
            commitReopenIssueMutation({
              environment,
              input: {
                issueId: data?.id,
              },
              onError: () =>
                addToast({
                  type: 'error',
                  message: ERRORS.couldNotCloseIssue,
                }),
              onCompleted: onNewIssueComment,
            })
          }
        }

        onSave()
      },
      [
        markdownBody,
        metaAndShiftKeysPressed,
        onSave,
        data.id,
        data.viewerCanClose,
        data.viewerCanReopen,
        environment,
        addToast,
        onNewIssueComment,
        closeButtonState,
        resetMarkdownBody,
      ],
    )

    const subject = useMemo<Subject>(() => {
      return {
        type: 'issue_comment',
        id: {
          id: data.id,
          databaseId: data.databaseId!,
        },
        repository: {
          databaseId: repositoryDatabaseId,
          nwo: data.repository.nameWithOwner,
          slashCommandsEnabled: data.repository.slashCommandsEnabled,
        },
      }
    }, [
      data.databaseId,
      data.id,
      data.repository.nameWithOwner,
      data.repository.slashCommandsEnabled,
      repositoryDatabaseId,
    ])

    const handleActionButtonUpdate = useCallback(() => {
      if (markdownValidationResult && markdownValidationResult.isValid) {
        onCommentSave(issueId)
      }
    }, [issueId, onCommentSave, markdownValidationResult])

    const actions = (
      <IssueActions
        actionRef={data}
        onAction={handleActionButtonUpdate}
        hasComment={markdownBody.length > 0}
        buttonSize="medium"
        closeButtonState={closeButtonState}
        setCloseButtonState={setCloseButtonState}
      />
    )

    return (
      <Box
        data-testid={TEST_IDS.commentComposer}
        sx={{
          mb: 2,
          mt: 4,
        }}
        ref={commentComposerRef}
        id={IDS.issueCommentComposer}
      >
        <Heading id="comment-composer-heading" as="h2" sx={{fontSize: 2, mb: 2}}>
          Add a comment
        </Heading>
        {IS_BROWSER &&
          (!data.viewerCanComment ? (
            <ReadonlyCommentBox
              reason={cantCommentReason}
              icon={LockIcon}
              {...testIdProps(TEST_IDS.readonlyCommentBox(TEST_IDS.commentComposer))}
            />
          ) : (
            <CommentBox
              ref={commentBoxRef}
              placeholder={LABELS.newCommentPlaceholder}
              label={LABELS.newComment}
              disabled={isSubmitting}
              subject={subject}
              value={markdownBody as SafeHTMLString}
              onChange={handleMarkdownBodyChanged}
              actions={actions}
              validationResult={markdownValidationResult}
              saveButtonText={BUTTON_LABELS.comment}
              saveButtonTrailingIcon={false}
              onSave={() => onCommentSave(issueId)}
              userSettings={commentBoxConfig}
              fileUploadsEnabled={fileUploadsEnabled}
              sx={markdownStyles}
              buttonSize="medium"
              labelledBy="comment-composer-heading"
              {...testIdProps(TEST_IDS.commentBox(TEST_IDS.commentComposer))}
            />
          ))}
        <LazyContributorFooter repositoryId={data.repository.id} />
      </Box>
    )
  },
)
IssueCommentComposer.displayName = 'CommentComposer'
